import fp from 'lodash/fp';
import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import FilterHeader from '../../webcommon/header/filterheader/FilterHeader';
import ColumnSelector from '../../webcommon/columnselector/ColumnSelector';
import Grid from '../../webcommon/grid/Grid';
import { findGridSort } from '../../common/helpermethods/HelperMethods';
import ToggleSwitch from '../../common/gridheader/toggleswitch/ToggleSwitch';

import GridHeader from '../../common/gridheader/GridHeader';
import styles from '../csasummary/CsaSummary.module.scss';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import * as appConstants from '../../common/constants';
import { PAGE_REFRESH_RATE } from './constants';


const MonitoringSummaryView = (props) => {
    const {
        initialise,
        useDollars,
        columns,
        setDrilldownFilters,
        refreshMonitoringSummary
    } = props;

    useEffect(() => {
        initialise();
        const continuousDataLoad = setInterval(() => {
            refreshMonitoringSummary();
        }, PAGE_REFRESH_RATE);
        return () => clearInterval(continuousDataLoad);
    }, [initialise, refreshMonitoringSummary]);

    const currentCurrency = useMemo(() => (useDollars ? 'USD' : 'EUR'), [useDollars]);

    const filterColumns = fp.filter(fp.get('visible'));

    const processDataForCsvExport = fp.map(row => ({
        EODDate: moment(row.businessDate).format(appConstants.dateFormat),
        'Monitoring Status': row.status.status,
        CSA: row.csa,
        Counterparty: row.counterparty,
        'Threshold Level 1 USD': row.monitoringThresholdLevel1Usd,
        'Threshold Level 2 USD': row.monitoringThresholdLevel2Usd,
        'Threshold Level 1 EUR': row.monitoringThresholdLevel1Eur,
        'Threshold Level 2 EUR': row.monitoringThresholdLevel2Eur,
        'IM Threshold Post USD': row.imThresholdPostUsd,
        'IM Threshold Post EUR': row.imThresholdPostEur,
        'IM Threshold Collect USD': row.imThresholdCollectUsd,
        'IM Threshold Collect EUR': row.imThresholdCollectEur,
        'IM Pledge USD': row.imPledgeUsd,
        'IM Receive USD': row.imReceiveUsd,
        'IM Pledge EUR': row.imPledgeEur,
        'IM Receive EUR': row.imReceiveEur,
        Comments: row.comments
    }));

    const calculateCsvFilename = (dateFrom, dateTo, csas) => {
        const title = 'MonitoringCSASummary';
        const filtered = csas.length ? 'filtered' : '';
        if (dateFrom && dateTo) {
            const startDate = moment(dateFrom).format(appConstants.dateFormat);
            const endDate = moment(dateTo).format(appConstants.dateFormat);
            return fp.compact([title, filtered, startDate, endDate]).join('_').concat('.csv');
        }
        const dateToday = moment().format(appConstants.dateFormat);
        return fp.compact([title, filtered, dateToday]).join('_').concat('.csv');
    };

    const setMonitoringSummaryColumns = fp.compose(
        props.setMonitoringSummariesColumns,
        fp.map(fp.pick(['id', 'visible'])),
    );

    const memoizedColumns = useMemo(() => filterColumns(columns), [columns, filterColumns]);

    const setDrilldownFiltersFunc = useCallback(row => setDrilldownFilters(
        row.csa, row.businessDate, row.counterparty
    ), [setDrilldownFilters]);

    const columnsWithSymbols = useMemo(() => columns.map(
        c => (c.hasSymbol
            ? fp.set('symbol', currentCurrency)(c)
            : c)
    ), [columns, currentCurrency]);

    const gridSortings = (newSorted) => {
        const idWithoutCurrency = fp.flow(
            fp.find(column => column.id === newSorted[0].id),
            fp.get('idWithoutCurrency')
        )(props.columns);
        const sortedWithStrippedId = idWithoutCurrency ? fp.set('[0].id', idWithoutCurrency)(newSorted) : newSorted;
        props.setGridSorts('monitoringSummary', sortedWithStrippedId);
    };

    const setDefaultSorts = useMemo(() => {
        const gridSorts = findGridSort(props.defaultSort, 'moniyoringSummary');
        return gridSorts.map((columnSort) => {
            const currencyId = fp.flow(
                fp.find(column => column.idWithoutCurrency === columnSort.id),
                fp.get('id')
            )(props.columns);
            return currencyId ? fp.set('id', currencyId)(columnSort) : columnSort;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defaultSort, props.useDollars, props.columns]);

    return (
        <div className={ styles.csaSummary }>
            <div className={ styles.headerWrapper }>
                <FilterHeader
                    title="Summary"
                    id="MonitoringSummary"
                />
                <div className={ styles.currencySelection }>
                    <ToggleSwitch
                        optionLeft="EUR"
                        optionRight="USD"
                        toggleIsLeft={ !props.useDollars }
                        toggleSwitch={ props.switchCurrencies }
                    />
                </div>
            </div>
            <div className={ styles.gridHeaderContainer }>
                <GridHeader
                    lastUpdated={ props.lastUpdated }
                    refreshControlsActive
                    refreshData={ props.refreshData }
                >
                    <ColumnSelector
                        columns={ columnsWithSymbols }
                        applyFilter={ setMonitoringSummaryColumns }
                    />
                    <div className={ styles.separator }>&nbsp;</div>
                    <ExportAsCsvButton
                        csvData={ props.rows }
                        csvFilename={
                            calculateCsvFilename(props.dateFrom, props.dateTo,
                                props.csas.length > 0 ? props.csas : [])
                        }
                        csvExportEmpty={ props.csvExportEmpty }
                        csvMapping={ processDataForCsvExport }
                    />
                </GridHeader>
            </div>
            <div className={ styles.gridContainer }>
                {props.columns.length ? (
                    <Grid
                        columns={ memoizedColumns }
                        defaultColumnSpacing={ 2 }
                        rows={ props.rows }
                        loading={ props.loading }
                        defaultSorted={ setDefaultSorts }
                        onSortedChange={ gridSortings }
                        onRowClick={ setDrilldownFiltersFunc }
                        symbol={ currentCurrency }
                    />
                ) : null}
            </div>
        </div>
    );
};

MonitoringSummaryView.propTypes = {
    initialise: PropTypes.func.isRequired,
    refreshData: PropTypes.func.isRequired,
    setMonitoringSummariesColumns: PropTypes.func.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string,
            type: PropTypes.string
        })
    ),
    defaultSort: PropTypes.arrayOf(
        PropTypes.shape({
            gridId: PropTypes.string,
            gridSort: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool
            }))
        })
    ),
    lastUpdated: PropTypes.number,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    loading: PropTypes.bool.isRequired,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    csas: PropTypes.arrayOf(PropTypes.string),
    csvExportEmpty: PropTypes.func.isRequired,
    setGridSorts: PropTypes.func.isRequired,
    setDrilldownFilters: PropTypes.func.isRequired,
    useDollars: PropTypes.bool.isRequired,
    switchCurrencies: PropTypes.func.isRequired
};

MonitoringSummaryView.defaultProps = {
    columns: [],
    rows: [],
    lastUpdated: moment().unix(),
    dateFrom: '',
    dateTo: '',
    csas: [],
    defaultSort: []
};

export default MonitoringSummaryView;
